/**
 * @fileoverview JSON-LD Structured Data Component
 *
 * Renders schema.org structured data as a <script type="application/ld+json"> tag.
 * Used on marketing pages to improve search engine understanding and rich results.
 */

interface JsonLdProps {
  data: Record<string, unknown>;
}

export function JsonLd({ data }: JsonLdProps) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/** Organization schema for MYNA AI */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "MYNA AI",
  url: "https://myna-ai.co",
  logo: "https://myna-ai.co/favicon.svg",
  description:
    "Intelligent business systems for service companies. AI phone, professional website, smart booking, reputation intelligence, and local SEO — all in one platform.",
  foundingDate: "2025",
  areaServed: {
    "@type": "Country",
    name: "United States",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    url: "https://myna-ai.co/demo",
  },
};

/** SoftwareApplication schema for MYNA AI Platform */
export const platformSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "MYNA AI Business Platform",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Custom AI-powered business operations platforms with AI phone system, professional website, smart calendar booking, reputation intelligence, and local SEO for service companies.",
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "USD",
    lowPrice: "999",
    highPrice: "1200",
    offerCount: "1",
  },
  featureList: [
    "24/7 AI phone system",
    "Professional website",
    "Smart calendar booking",
    "Reputation intelligence",
    "Google 3-Pack local SEO",
    "Business command center dashboard",
  ],
  provider: {
    "@type": "Organization",
    name: "MYNA AI",
    url: "https://myna-ai.co",
  },
};

/** Pricing page schema with offers */
export const pricingSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "MYNA AI",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Intelligent business systems platform for service companies — AI phone, website, booking, reputation, and local SEO.",
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "USD",
    lowPrice: "999",
    highPrice: "1200",
    offerCount: "1",
    description:
      "Custom AI-powered business operations platform with AI receptionist, professional website, lead pipeline, review intelligence, and more.",
  },
  provider: {
    "@type": "Organization",
    name: "MYNA AI",
    url: "https://myna-ai.co",
  },
};

/** FAQ schema for Google rich results on the pricing page */
export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What does 'starting from' mean?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Every platform is custom-scoped based on your business needs. $999/mo is the starting price for our core platform. After a discovery call, we'll provide exact pricing based on your requirements, integrations, and customization needs.",
      },
    },
    {
      "@type": "Question",
      name: "What's the difference between Annual and Month-to-Month?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Annual plans save $201/mo ($2,412/yr). Both include setup fees. Annual locks in your rate for 12 months. Month-to-Month offers flexibility with 30-day cancellation notice.",
      },
    },
    {
      "@type": "Question",
      name: "What's included in the setup fee?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Custom AI receptionist training for your business, professional website build, Google Calendar integration, review intelligence setup, content configuration, and dedicated onboarding support. Setup fees vary by project scope.",
      },
    },
    {
      "@type": "Question",
      name: "How quickly can I be up and running?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most platforms go live within 5-7 business days from signup. We need minimal input: business info, logo, calendar email, business hours, and service details. We handle everything else.",
      },
    },
    {
      "@type": "Question",
      name: "Can the AI handle industry-specific questions?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. Your AI is custom-trained on your specific services, pricing, compliance requirements, and business processes. It handles inquiries, educates customers, and books appointments — all while following your guidelines.",
      },
    },
    {
      "@type": "Question",
      name: "Can I cancel my subscription?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Month-to-Month plans can be canceled anytime with 30 days notice. Annual plans have a 12-month commitment with early termination fees. We're confident you'll see ROI within the first month.",
      },
    },
  ],
};
